import { useIfpAnalyticsService } from '@sec-loans-ui/ifp-loans/analytics/analyticsContext'
import { FormContextValue } from '@sec-loans-ui/ifp-loans/types'
import { secLoansExit } from '@sec-loans-ui/utils/helpers'
import { assertValue } from '@shared/helpers'
import { getOpenAbortModalEvent, getOpenErrorPromptModalEvent, OpenModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { getLanguage, isComingBackFromSigning } from '@ui-common/utils'
import { shallowEqual, useSelector } from '@xstate/react'
import { use, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { ActorRefFrom, SnapshotFrom, StateFrom } from 'xstate'

import { getIfpExitDuringSendStepModalEvent } from './modals/getIfpExitDuringSendStepModalEvent'
import { IfpWizardData } from './types'
import { useFormStepState } from './useFormStepState'
import { createWizardMachine, EditableStep, WizardContext, WizardMachineType, WizardStep } from './wizardMachine'

const selectValues = ({
  context: {
    user,
    wizardData,
    isEditing,
    authToken,
    applicationId,
    loan,
    currentApplicantInfoIndex,
    wizardType,
    isWizardComplete,
    isFinalizationPhase,
    actionType,
    applicationState,
    shouldShowSentModal,
    hasSendError,
    isPrimaryApplicant,
    sendError,
    actionsNeeded,
  },
  value,
}: SnapshotFrom<WizardMachineType>) => ({
  applicationId,
  user,
  wizardData,
  wizardType,
  isEditing,
  authToken,
  loan,
  step: value,
  currentApplicantInfoIndex,
  isWizardComplete,
  isFinalizationPhase,
  actionType,
  applicationState,
  shouldShowSentModal,
  hasSendError,
  isPrimaryApplicant,
  sendError,
  actionsNeeded,
})

interface UseWizardLogicReturn extends FormContextValue {
  actor: ActorRefFrom<WizardMachineType>
}

const shouldHydrateState = (state: StateFrom<WizardMachineType>) => {
  const isSignCallback = isComingBackFromSigning() && state?.value === WizardStep.SendStep
  return isSignCallback
}

export const useWizardLogic = (
  initialContext: Partial<WizardContext>,
  onCancelledApplication: () => void,
  onReturnedApplication: () => void,
): UseWizardLogicReturn => {
  const intl = useIntl()
  const { analyticsService } = useIfpAnalyticsService()

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const wizardMachine = createWizardMachine({ onCancelledApplication, onReturnedApplication })
  const actor = usePersistingActor<WizardMachineType>(
    wizardMachine,
    shouldHydrateState,
    undefined,
    ensureInitialContextIsValid(initialContext),
  )
  const send = actor.send

  const nextStep = useCallback(
    (data: Partial<IfpWizardData>) => {
      send({ type: 'NEXT_STEP', data })
    },
    [send],
  )

  const onEdit = useCallback(
    (step: EditableStep) => {
      analyticsService.sendPageEditActionEvent(step)
      send({ type: 'EDIT', step })
    },
    [send, analyticsService],
  )

  const onEditApplicantInfo = useCallback(
    (index: number) => {
      analyticsService.sendPageEditActionEvent(WizardStep.ApplicantInfoStep, index)
      send({ type: 'EDIT_APPLICANT_INFO', index })
    },
    [send, analyticsService],
  )

  const submitApplication = useCallback(() => {
    send({ type: 'SUBMIT' })
  }, [send])

  const onCancelApplication = useCallback(() => {
    send({ type: 'CANCEL_APPLICATION' })
  }, [send])

  const onReturnApplication = useCallback(() => {
    send({ type: 'RETURN_APPLICATION' })
  }, [send])

  const {
    user,
    wizardData,
    isEditing,
    authToken,
    applicationId,
    loan,
    step,
    currentApplicantInfoIndex,
    wizardType,
    isWizardComplete,
    isFinalizationPhase,
    actionType,
    applicationState,
    shouldShowSentModal,
    hasSendError,
    sendError,
    isPrimaryApplicant,
    actionsNeeded,
  } = useSelector(actor, selectValues, shallowEqual)

  const exit = useCallback(async () => {
    analyticsService.sendWizardStepCustomEvent(step, 'ExitApp')
    await secLoansExit({ language: getLanguage(), isSuccess: true })
  }, [analyticsService, step])

  const goBackToStatus = useCallback(() => {
    analyticsService.sendWizardStepCustomEvent(step, 'GoToStatusLink')
    send({ type: 'BACK_TO_STATUS' })
  }, [send, analyticsService, step])

  const {
    isSubmitting: isStepSubmitting,
    back,
    submit: _submitStep,
    hasSubmitFailed,
    error: formStepError,
  } = useFormStepState(applicationId, loan, {
    toSummary: goBackToStatus,
    previous: useCallback(() => {
      send({ type: 'PREVIOUS_STEP' })
    }, [send]),
    proceed: nextStep,
    exit,
  })

  const submitStep = useCallback(
    (data: Partial<IfpWizardData>) => {
      _submitStep({ ...wizardData, ...data })
    },
    [_submitStep, wizardData],
  )

  const showSendAbortPromptModal = useCallback(() => {
    const openEvent: OpenModalEvent = getIfpExitDuringSendStepModalEvent({
      onAbort: () => {
        analyticsService.sendCustomEvent('SendAbortModal', 'ConfirmButton')
        void exit()
      },
      onCancel: () => analyticsService.sendCustomEvent('SendAbortModal', 'CancelButton'),
      intl,
    })
    analyticsService.sendModalViewEvent('SendAbortModal')
    modalSend(openEvent)
  }, [analyticsService, exit, intl, modalSend])

  const openErrorModal = useCallback(
    (requestId?: string) => {
      analyticsService.sendModalViewEvent('ErrorModal')
      modalSend(
        getOpenErrorPromptModalEvent(
          () => {
            analyticsService.sendCustomEvent('ErrorModal', 'ConfirmButton')
          },
          intl,
          undefined,
          undefined,
          undefined,
          requestId,
        ),
      )
    },
    [analyticsService, intl, modalSend],
  )

  const showSubmitErrorModal = useCallback(() => {
    openErrorModal(formStepError?.requestId)
  }, [openErrorModal, formStepError?.requestId])

  const showSendErrorModal = useCallback(() => {
    openErrorModal(sendError?.requestId)
  }, [openErrorModal, sendError?.requestId])

  const exitWithConfirmDialog = () => {
    analyticsService.sendModalViewEvent('ModalQuit')
    modalSend(
      getOpenAbortModalEvent(() => {
        analyticsService.sendCustomEvent('ModalQuit', 'ConfirmButton')
        void exit()
      }, intl),
    )
  }

  return {
    actor,
    applicationId,
    authToken,
    step,
    user,
    loan,
    wizardData,
    isEditing,
    isStepSubmitting,
    hasSubmitFailed,
    currentApplicantInfoIndex,
    wizardType,
    isWizardComplete,
    isFinalizationPhase,
    actionType,
    applicationState,
    shouldShowSentModal,
    hasSendError,
    isPrimaryApplicant,
    actionsNeeded,
    exit,
    exitWithConfirmDialog,
    goBackToStatus,
    goBack: () => back(false),
    proceed: useCallback(
      (data: Partial<IfpWizardData>) => {
        step === WizardStep.StatusStep ? nextStep(data) : submitStep(data)
      },
      [nextStep, step, submitStep],
    ),
    onEdit,
    onEditApplicantInfo,
    submit: submitApplication,
    showSendAbortPromptModal,
    showSendErrorModal,
    onCancelApplication,
    onReturnApplication,
    showSubmitErrorModal,
  }
}

const ensureInitialContextIsValid = (initialContext: Partial<WizardContext>): WizardContext => ({
  ...initialContext,
  user: assertValue(initialContext.user, 'initialContext.user'),
  applicationId: assertValue(initialContext.applicationId, 'initialContext.applicationId'),
  applicationState: assertValue(initialContext.applicationState, 'initialContext.applicationState'),
  loan: assertValue(initialContext.loan, 'initialContext.loan'),
  isNewApplication: assertValue(initialContext.isNewApplication, 'initialContext.isNewApplication'),
  currentApplicantInfoIndex: 0,
})
